import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import NotFound from "pages/NotFound";
import InfluencerProfile from './pages/influencer-profile';
import PaymentCenter from './pages/payment-center';
import InfluencerDashboard from './pages/influencer-dashboard';
import InfluencerDiscovery from './pages/influencer-discovery';
import ArtistDashboard from './pages/artist-dashboard';
import CampaignManagement from './pages/campaign-management';
import ArtistProfileCreation from './pages/artist-profile-creation';
import InfluencerProfileCreation from './pages/influencer-profile-creation';
import Register from './pages/register';
import ProfileSetupChecklist from './pages/profile-setup-checklist';
import PaymentSetup from './pages/payment-setup';
import SocialLinksSetup from './pages/social-links-setup';
import NotificationCenter from './pages/notification-center';
import AnalyticsDashboard from './pages/analytics-dashboard';
import CampaignCreationWizard from './pages/campaign-creation-wizard';
import MessagingHub from './pages/messaging-hub';
import LoginModal from './components/ui/LoginModal';
import SmartLinkPage from './pages/smart-link';
import CampaignLinkManager from './pages/campaign-link-manager';
import MusicLinkAnalytics from './pages/music-link-analytics';
import InfluencerClickAnalytics from './pages/influencer-click-analytics';
import { useAuth } from './contexts/AuthContext';

// ProtectedRoute: opens login modal instead of redirecting
const ProtectedRoute = ({ children }) => {
  const { user, loading, isLoginModalOpen, openLoginModal, closeLoginModal } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Show discovery page behind the login modal
    if (!isLoginModalOpen) {
      openLoginModal();
    }
    return (
      <>
        <InfluencerDiscovery />
        <LoginModal isOpen={true} onClose={closeLoginModal} />
      </>
    );
  }

  return children;
};

const Routes = () => {
  const { isLoginModalOpen, closeLoginModal } = useAuth();

  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* PUBLIC ROUTES — accessible to everyone without login */}
          <Route path="/" element={<InfluencerDiscovery />} />
          <Route path="/discover" element={<Navigate to="/" replace />} />
          <Route path="/influencers" element={<InfluencerDiscovery />} />
          <Route path="/campaigns" element={<CampaignManagement />} />
          <Route path="/campaign-management" element={<CampaignManagement />} />
          <Route path="/influencer/:slug" element={<InfluencerProfile />} />
          <Route path="/influencer-profile" element={<InfluencerProfile />} />

          {/* Smart Link public landing page */}
          <Route path="/go/:slug" element={<SmartLinkPage />} />

          {/* Redirect old login route to home */}
          <Route path="/influencer-login" element={<Navigate to="/" replace />} />
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="/register" element={<Register />} />

          {/* PRIVATE ROUTES — only for authenticated users */}
          <Route path="/influencer-dashboard" element={<ProtectedRoute><InfluencerDashboard /></ProtectedRoute>} />
          <Route path="/profile/edit" element={<ProtectedRoute><InfluencerProfileCreation /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><MessagingHub /></ProtectedRoute>} />
          <Route path="/payment-center" element={<ProtectedRoute><PaymentCenter /></ProtectedRoute>} />
          <Route path="/artist-dashboard" element={<ProtectedRoute><ArtistDashboard /></ProtectedRoute>} />
          <Route path="/campaign-creation-wizard" element={<ProtectedRoute><CampaignCreationWizard /></ProtectedRoute>} />
          <Route path="/messaging-hub" element={<ProtectedRoute><MessagingHub /></ProtectedRoute>} />
          <Route path="/artist-profile-creation" element={<ProtectedRoute><ArtistProfileCreation /></ProtectedRoute>} />
          <Route path="/influencer-profile-creation" element={<ProtectedRoute><InfluencerProfileCreation /></ProtectedRoute>} />
          <Route path="/profile-setup-checklist" element={<ProtectedRoute><ProfileSetupChecklist /></ProtectedRoute>} />
          <Route path="/payment-setup" element={<ProtectedRoute><PaymentSetup /></ProtectedRoute>} />
          <Route path="/social-links-setup" element={<ProtectedRoute><SocialLinksSetup /></ProtectedRoute>} />
          <Route path="/notification-center" element={<ProtectedRoute><NotificationCenter /></ProtectedRoute>} />
          <Route path="/analytics-dashboard" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
          <Route path="/music-links" element={<ProtectedRoute><CampaignLinkManager /></ProtectedRoute>} />
          <Route path="/music-analytics" element={<ProtectedRoute><MusicLinkAnalytics /></ProtectedRoute>} />
          <Route path="/click-analytics" element={<ProtectedRoute><InfluencerClickAnalytics /></ProtectedRoute>} />

          <Route path="*" element={<NotFound />} />
        </RouterRoutes>

        {/* Global login modal — can be triggered from anywhere */}
        <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
